import { gql, useMutation } from "@apollo/client";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { uuidValidator } from "../../../../../packages/mt-commands/helpers/validateUuid";
import Button from "../../Atoms/Button";
import InputEmail from "../../Atoms/InputEmail";
import ContactFormFields from "./ContactForm";
import { categoryFields, categoryText } from "./ContactForm/contactFormData";
import styles from "./ContactUs.module.scss";
import OptionsSelector from "./SelectMenus/OptionsSelector";
import ThankYou from "./ThankYou";

export interface FormDataType {
  your_name?: string;
  booking_id?: string;
  tour_name?: string;
  inquiry_details?: string;
  requested_tour_date?: string;
  number_of_guests?: string;
  is_connected_to_bokun?: string;
  company_url?: string;
  country_of_residence?: string;
  type_of_partnership?: string;
  type_of_promotion?: string;
  message?: string;
}

export default function ContactUsTemplate() {
  const router = useRouter();
  const { category } = router.query;
  const [email, setEmail] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormDataType>({});
  const [isDetailStep, setIsDetailStep] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formFields = categoryFields[selectedCategory];
  const textContent = categoryText[selectedCategory];

  useEffect(() => {
    if (category) {
      setSelectedCategory(decodeURIComponent(category as string));
    }
  }, [category]);

  const [sendInquiry] = useMutation(SEND_INQUIRY);

  const handleInputChange = (field: keyof FormDataType, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNextClick = () => {
    if (selectedCategory) {
      setIsDetailStep(true);
    }
  };

  const handleBackClick = () => {
    setIsDetailStep(false);
    setSelectedCategory(null);
    setFormData({});
  };

  const header = isDetailStep ? selectedCategory : "Contact Us";
  const buttonStyle = {
    width: "100px",
    fontSize: "16px",
    margin: "25px 0 30px 0",
  };

  return (
    <>
      <div className={styles.contactFormContainer}>
        {!isSubmitted && (
          <p className={styles.progessIndicator}>{!isDetailStep ? 1 : 2}/2</p>
        )}
        <div style={{ display: "flex", flexDirection: "row" }}>
          <h2>{header}</h2>
        </div>
        <div className={styles.inputContainer}>
          {!isSubmitted ? (
            <>
              {!isDetailStep && (
                <>
                  <h3 style={{ marginBottom: "5px" }}>
                    Email
                    <RequiredMarker />
                  </h3>
                  <InputEmail
                    value={email}
                    required
                    onChange={(value) => setEmail(value)}
                    placeholder="Your Email"
                  />
                  <h3 style={{ marginTop: "25px" }}>
                    Inquiry Reason
                    <RequiredMarker />
                  </h3>
                  <OptionsSelector
                    value={selectedCategory || ""}
                    options={Object.keys(categoryFields)}
                    placeholder="Select an Inquiry Reason"
                    onChange={(value) => {
                      setSelectedCategory(value);
                    }}
                  />
                </>
              )}

              {isDetailStep && selectedCategory && (
                <>
                  {textContent && <p>{textContent}</p>}
                  {[
                    "Tour Cancellation or Refund Request",
                    "Tour Rescheduling",
                  ].includes(selectedCategory) && (
                    <div style={{ marginTop: "20px" }}>
                      <ContactFormFields
                        fields={["your_name", "booking_id", "message"]}
                        formData={formData}
                        onInputChange={handleInputChange}
                        selectedCategory={selectedCategory}
                      />
                    </div>
                  )}
                  {![
                    "Tour Cancellation or Refund Request",
                    "Tour Rescheduling",
                  ].includes(selectedCategory) &&
                    formFields?.length > 0 && (
                      <ContactFormFields
                        fields={formFields}
                        formData={formData}
                        onInputChange={handleInputChange}
                        selectedCategory={selectedCategory}
                      />
                    )}
                </>
              )}
              <div className={styles.buttonContainer}>
                {isDetailStep ? (
                  <>
                    <Button small style={buttonStyle} onClick={handleBackClick}>
                      Back
                    </Button>
                    {(!textContent || formFields?.length > 0) && (
                      <Button
                        small
                        style={buttonStyle}
                        onClick={handleSubmit}
                        disabled={!canSubmit()}
                      >
                        Submit
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    small
                    style={buttonStyle}
                    onClick={handleNextClick}
                    disabled={!validateEmail(email) || !selectedCategory}
                  >
                    Next
                  </Button>
                )}
              </div>
              {formData.booking_id && !uuidValidator(formData.booking_id) && (
                <p className={styles.errorMessage}>* Invalid Booking ID</p>
              )}
              {!validateEmail(email) && email && selectedCategory && (
                <p className={styles.errorMessage}>* Invalid Email</p>
              )}
            </>
          ) : (
            <ThankYou />
          )}
        </div>
      </div>
    </>
  );

  async function handleSubmit() {
    const variables = {
      input: {
        email,
        category: selectedCategory,
        name: formData.your_name || "",
        bookingId: formData.booking_id || "",
        productName: formData.tour_name || "",
        subCategory: formData.inquiry_details || "",
        requestedTourDate: formData.requested_tour_date || null,
        numberOfGuests: parseInt(formData.number_of_guests, 10) || null,
        typeOfPartnership: formData.type_of_partnership || null,
        isConnectedToBokun:
          formData.type_of_partnership === "OTA"
            ? formData.is_connected_to_bokun
            : null,
        typeOfPromotion: formData.type_of_promotion || null,
        companyUrl: formData.company_url || null,
        countryOfResidence: formData.country_of_residence || null,
        message: formData.message || "",
      },
    };
    await sendInquiry({ variables });
    setIsSubmitted(true);
    setEmail("");
    setSelectedCategory(null);
    setFormData({});
  }

  function canSubmit(): boolean {
    const categoriesThatDoNotRequireBookingId = [
      "Tour Availability",
      "Private Tour Inquiry",
      "Payment Issues",
      "Tour Content Details",
      "Business & Partnership Inquiry",
      "Media Inquiries (取材)",
      "Other",
    ];
    if (formData.message?.length > 500) return false;
    if (
      !categoriesThatDoNotRequireBookingId.includes(selectedCategory) &&
      !uuidValidator(formData.booking_id)
    )
      return false;
    const requiredFields = formFields || [];
    return requiredFields.every((field: string) => {
      if (field === "booking_id" && selectedCategory === "Other") {
        return true;
      }
      if (
        formData.type_of_partnership !== "OTA" &&
        field === "is_connected_to_bokun"
      ) {
        return true;
      }
      return formData[field] && formData[field].trim() !== "";
    });
  }

  function validateEmail(email: string) {
    return email.match(/^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/);
  }
}

function RequiredMarker() {
  return <span style={{ color: "red" }}>*</span>;
}

const SEND_INQUIRY = gql`
  mutation SendInquiry($input: SendInquiryInput!) {
    sendInquiry(input: $input) {
      id
      created
      email
      name
      bookingId
      productName
      category
      subCategory
      requestedTourDate
      numberOfGuests
      isConnectedToBokun
      companyUrl
      countryOfResidence
      typeOfPartnership
      typeOfPromotion
      message
    }
  }
`;
